import { throwIfAborted } from '../codecs/types';
import { AddLayerCommand, cloneLayer, rejected, type Command, type CommandExecution } from '../core/commands';
import type { Matrix } from '../core/geometry';
import { findLayer, findLayerParent, layerDocumentTransform, walkLayers, type EditorDocument, type Layer } from '../core/model';
import type { DocumentSession } from '../core/session';
import { captureLayerResources, layerNeedsResources, type LayerResources } from './layer-resources';
import { nativeDocumentTextPaint, verifyNativeDocumentText } from './native-text';
import { hasNativePortableText } from './native-text-contract';
import { stageResourceLayer } from './transfer-layer';

export interface LayerClipboard {
  readonly documentId: string;
  readonly layer: Layer;
  readonly resources?: LayerResources;
}

/** Own a detached snapshot but preserve layer IDs for source snapshot admission. */
function snapshot(layer: Layer): Layer {
  const copy = cloneLayer(layer, { portableText: 'prepare' });
  const restoreIds = (original: Layer, copied: Layer): void => {
    copied.id = original.id;
    if (original.kind === 'group' && copied.kind === 'group') original.children.forEach((child, index) => restoreIds(child, copied.children[index]!));
  };
  restoreIds(layer, copy); return copy;
}

/** The clipboard remains usable after the source document closes. */
export function captureLayerClipboard(document: EditorDocument, layer: Layer, options: { documentCoordinates?: boolean } = {}): LayerClipboard {
  for (const item of layer.kind === 'group' ? walkLayers(layer) : [layer]) {
    if (item.kind === 'text' && item.text.schemaVersion === 2) nativeDocumentTextPaint(document, item);
  }
  const copy = snapshot(layer);
  if (options.documentCoordinates !== false) copy.transform = layerDocumentTransform(document.root, layer.id) ?? copy.transform;
  return { documentId: document.id, layer: copy, ...(layerNeedsResources(layer) ? { resources: captureLayerResources(document) } : {}) };
}

/** Admit the owned source, then bind new IDs/font references in a staged destination. */
export interface PasteLayerOptions { parentId?: string; index?: number; transform?: Matrix; clipping?: boolean; label?: string; recordDuplicate?: boolean; signal?: AbortSignal }

/**
 * A pasted layer prepared for a synchronous commit: its copy has new ids
 * (`ids` maps every source layer id to its copy's), and resource-bearing
 * copies are staged against the target (see `stageResourceLayer`).
 */
export interface StagedLayerPaste {
  readonly layer: Layer;
  readonly ids: Readonly<Record<string, string>>;
  current(target: DocumentSession, document: EditorDocument): boolean;
  command(document: EditorDocument, parentId: string, index: number): Command;
  release(): void;
}

function copyIds(source: Layer, copy: Layer, ids: Record<string, string> = {}): Record<string, string> {
  ids[source.id] = copy.id;
  if (source.kind === 'group' && copy.kind === 'group') source.children.forEach((child, index) => { const twin = copy.children[index]; if (twin) copyIds(child, twin, ids); });
  return ids;
}

/** Admit the owned source and bind new IDs/font references in a staged destination, without committing. */
export async function stagePasteLayerClipboard(clipboard: LayerClipboard, target: DocumentSession, options: Omit<PasteLayerOptions, 'parentId' | 'index'> = {}): Promise<StagedLayerPaste | { rejected: string }> {
  options = { ...options, ...(options.transform ? { transform: [...options.transform] as unknown as Matrix } : {}) };
  throwIfAborted(options.signal);
  const binding = target.captureRevision();
  const source = snapshot(clipboard.layer);
  const resources = clipboard.resources && structuredClone(clipboard.resources);
  const portable = hasNativePortableText(source);
  if (layerNeedsResources(source) && !resources) throw new Error('Copied layer resources are missing.');
  const sourceDocument: EditorDocument = { ...target.document, id: clipboard.documentId,
    root: { ...target.document.root, children: [source] }, extensions: resources ? { 'maipo:public-document': { format: 'maipo-scene', version: '0.1', revision: 0, ...resources } } : {} };
  const controller = new AbortController();
  const abort = () => controller.abort();
  options.signal?.addEventListener('abort', abort, { once: true });
  target.closedSignal.addEventListener('abort', abort, { once: true });
  if (options.signal?.aborted || target.closedSignal.aborted) abort();
  try {
    if (portable) {
      if (!resources) throw new Error('Portable clipboard font resources are missing.');
      await verifyNativeDocumentText(sourceDocument, controller.signal);
    }
    throwIfAborted(controller.signal);
    if (!target.isCurrent(binding) || target.history.hasLiveCommand) return { rejected: 'The destination changed while the copy was prepared. Try again.' };
    const copy = cloneLayer(source, { portableText: 'prepare' });
    if (options.transform) copy.transform = [...options.transform] as unknown as Matrix;
    if (options.clipping !== undefined) copy.clipping = options.clipping;
    const ids = copyIds(source, copy);
    if (resources) {
      const staged = await stageResourceLayer(resources, target, copy,
        { rebindPortableText: portable, recordDuplicate: options.recordDuplicate ?? false, signal: controller.signal, ...(options.label ? { label: options.label } : {}) });
      return 'rejected' in staged ? staged : { ...staged, layer: copy, ids };
    }
    // The plain path binds to the revision captured above, as the commit always did.
    const current = (session: DocumentSession) => session === target && session.isCurrent(binding) && !session.history.hasLiveCommand;
    return { layer: copy, ids, current, command: (_document, parentId, index) => new AddLayerCommand(copy, parentId, index), release: () => undefined };
  } finally {
    options.signal?.removeEventListener('abort', abort); target.closedSignal.removeEventListener('abort', abort);
  }
}

/** Admit the owned source, then bind new IDs/font references in a staged destination. */
export async function pasteLayerClipboard(clipboard: LayerClipboard, target: DocumentSession, options: PasteLayerOptions = {}): Promise<CommandExecution> {
  // A plain layer (no resources, no portable text) commits before the first
  // await, as it did before staging existed: an edit made right after ⌘J
  // cannot slip in between, and action replay sees the duplicate at once.
  if (!clipboard.resources && !layerNeedsResources(clipboard.layer) && !hasNativePortableText(clipboard.layer)) {
    throwIfAborted(options.signal);
    target.captureRevision();
    const copy = cloneLayer(snapshot(clipboard.layer), { portableText: 'prepare' });
    if (options.transform) copy.transform = [...options.transform] as unknown as Matrix;
    if (options.clipping !== undefined) copy.clipping = options.clipping;
    return target.history.execute(target.document, new AddLayerCommand(copy, options.parentId, options.index));
  }
  const staged = await stagePasteLayerClipboard(clipboard, target, options);
  if ('rejected' in staged) return rejected(staged.rejected);
  try {
    if (!staged.current(target, target.document)) return rejected('The destination changed while resources were prepared. Try again.');
    return target.history.execute(target.document, staged.command(target.document, options.parentId ?? 'root', options.index ?? 0));
  } finally {
    staged.release();
  }
}

/** Where ⌘J puts a duplicate: directly above the source, in the same parent. */
export function duplicatePlacement(document: EditorDocument, layerId: string): { parentId: string; index: number } {
  const location = findLayerParent(document.root, layerId);
  return { parentId: location?.parent === document.root ? 'root' : location?.parent.id ?? 'root', index: location?.index ?? 0 };
}

/** The clipboard ⌘J pastes: the layer in its parent's coordinates, named "<name> copy". */
export function duplicateClipboard(document: EditorDocument, layer: Layer): LayerClipboard {
  const clipboard = captureLayerClipboard(document, layer, { documentCoordinates: false });
  clipboard.layer.name = layer.name.slice(0, 4091).replace(/[\ud800-\udbff]$/, '') + ' copy';
  return clipboard;
}

export async function duplicateLayerInSession(session: DocumentSession, layerId: string, options: { signal?: AbortSignal } = {}): Promise<CommandExecution> {
  const layer = findLayer(session.document.root, layerId);
  if (!layer) return rejected('The source layer no longer exists.');
  const placement = duplicatePlacement(session.document, layerId);
  const clipboard = duplicateClipboard(session.document, layer);
  return pasteLayerClipboard(clipboard, session, { ...options, ...placement, label: `Duplicate ${layer.name}`, recordDuplicate: true });
}
